// PDF, print and Send to EMR for staff-completed forms (internal forms, staff
// sections, amendments). react-pdf is only ever loaded on demand (staff/emrPdf
// imports the renderer dynamically).
import { formwizApi } from "../staff/formwizApi";
import { renderEmrPdf } from "../staff/emrPdf";
import { errInfo } from "../staff/staffUtils";

// InternalDetail (or a SubmissionDetail) -> the renderer / FormView / PDF
// response, with the staff lines:
//   completedVia  {via:"staff", by} (internal form) | {via:"kiosk", by} (tablet)
//   staffSections {by, at}           staff completed a patient form's staff parts
//   amendment     {documentId, filedAt, reason}
// opts.answers / opts.lang: render what is about to be saved (not yet on the server).
export function buildStaffResponse(detail, def, opts = {}) {
  const d = detail || {};
  const staff = d.staff || {};
  const via = d.completed_via || null;
  const out = {
    formId: d.form_id || (def && def.id),
    formVersion: d.form_version || (def && def.version),
    lang: opts.lang || d.language || "en",
    answers: opts.answers || d.answers || {},
    scores: opts.answers ? null : d.scores || null,
    startedAt: d.started_at || null,
    completedAt: opts.completedAt || (opts.answers ? new Date().toISOString() : d.submitted_at || null),
  };
  // The first save names the person saving (opts.currentUser); later saves
  // keep the first saver, as the server records it (finalized_by).
  const staffBy = staff.finalized_by || opts.currentUser || staff.created_by || null;
  if (via && via.via === "internal") out.completedVia = { via: "staff", by: via.by || staffBy };
  else if (via && via.via === "kiosk") out.completedVia = { via: "kiosk", by: via.handed_over_by || null };
  else if (!via && (staff.kind === "internal" || staff.kind === "amendment")) out.completedVia = { via: "staff", by: staffBy };
  if (opts.staffSectionsBy) out.staffSections = { by: opts.staffSectionsBy, at: out.completedAt };
  else if (d.staff_sections && d.staff_sections.by) out.staffSections = { by: d.staff_sections.by, at: d.staff_sections.at || null };
  const am = staff.amends;
  if (am) out.amendment = { documentId: am.emr_document_id || null, filedAt: am.emr_posted_at || null, reason: am.reason || "" };
  return out;
}

// The filed PDF: the same renderer and language rule as patient forms
// (consent/policy/abn in the language used, everything else in English).
export function renderStaffPdf(def, response, prefill) {
  return renderEmrPdf(def, response, prefill || {});
}

const asPdf = (blob) => (blob && blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" }));

// The PDF staff print / save: the stored copy when it matches the current
// answers (pdf_current), else rendered here from the server-held answers.
export async function staffPdfBlob(detail, def) {
  const staff = (detail && detail.staff) || {};
  const stored = staff.pdf_current || (detail && detail.has_pdf && detail.emr_status === "posted");
  if (stored) {
    try {
      const b = await formwizApi.submissionPdf(detail.id);
      if (typeof Blob !== "undefined" && b instanceof Blob && b.size > 0) return asPdf(b);
    } catch (_) {
      /* render below */
    }
  }
  return asPdf(await renderStaffPdf(def, buildStaffResponse(detail, def), detail.prefill || {}));
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// Print through a hidden iframe (the browser's print dialog for the PDF);
// when the browser cannot print a PDF in a frame, open it in a new tab.
export function printBlob(blob) {
  const url = URL.createObjectURL(blob);
  const fallback = () => {
    try {
      window.open(url, "_blank");
    } catch (_) {
      /* popup blocked: nothing else to do */
    }
  };
  try {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";
    frame.onload = () => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch (_) {
        fallback();
      }
    };
    frame.src = url;
    document.body.appendChild(frame);
    setTimeout(() => {
      frame.remove();
      URL.revokeObjectURL(url);
    }, 120000);
  } catch (_) {
    fallback();
  }
}

/*
 * Send to EMR with the staff-rendered PDF. The server answers 409
 * pdf_required until a staff PDF bound to the current answers is stored: render
 * it here, upload it with the revision it was rendered from, and post again.
 * A 409 stale_revision (someone saved meanwhile) re-reads the submission once
 * and renders again.
 *   toResponse(detail, def) -> the response to render (default buildStaffResponse)
 *   reload() -> the fresh detail (for stale_revision)
 */
export async function sendWithPdf({ detail, def, onStep, toResponse = buildStaffResponse, reload }) {
  try {
    return await formwizApi.sendToEmr(detail.id);
  } catch (e) {
    const info = errInfo(e);
    if (!(info.status === 409 && info.code === "pdf_required")) throw e;
  }
  let cur = detail;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    if (onStep) onStep("Rendering the PDF…");
    const blob = await renderStaffPdf(def, toResponse(cur, def), cur.prefill || {});
    if (onStep) onStep("Uploading the PDF…");
    try {
      await formwizApi.uploadPdf(cur.id, blob, (cur.staff && cur.staff.revision) || 0);
      break;
    } catch (e) {
      const info = errInfo(e);
      if (attempt === 0 && info.status === 409 && info.code === "stale_revision" && reload) {
        cur = { ...cur, ...(await reload()) };
        continue;
      }
      throw e;
    }
  }
  if (onStep) onStep("Sending to the EMR…");
  return formwizApi.sendToEmr(cur.id);
}
